import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  LinearProgress,
  Stack,
  TablePagination,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { getPlatformGlobalPassengerVendas } from "../../../api/passagensApi";
import { resolveMpFeeAmount } from "../../Passagens/passagemMpFeeUi";
import PassengerPaymentCell from "../../Passagens/passagemMpPaymentUi";
import PassengerSaleValueCell from "./PassengerSaleValueCell";
import { AnalyticsDataTable } from "./platformConsoleAnalyticsUi";
import { formatProductUsagePeriodBR } from "../../../utils/productUsageScreenLabel";
import { PASSENGER_KPI_METRICS } from "./passengerTaxConstants";
import { fmtChargebackLabel } from "./shared";

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtDateTime(v) {
  if (!v) return "—";
  return new Date(v).toLocaleString("pt-BR");
}

function fmtDateOnly(v) {
  if (!v) return "—";
  return new Date(v).toLocaleDateString("pt-BR");
}

function fmtBool(v) {
  if (v == null || v === "") return "—";
  if (v === true || v === 1 || v === "1" || String(v).toLowerCase() === "true") return "Sim";
  if (v === false || v === 0 || v === "0" || String(v).toLowerCase() === "false") return "Não";
  return "—";
}

function fmtCoverage(row, t) {
  if (row.chargeback_coverage_applied != null) {
    return row.chargeback_coverage_applied
      ? t("platformPage.passengerMpAuditCoverageApplied", { defaultValue: "Cobertura aplicada" })
      : t("platformPage.passengerMpAuditCoverageNotApplied", { defaultValue: "Sem cobertura" });
  }
  if (row.chargeback_coverage_elegible != null) {
    return row.chargeback_coverage_elegible
      ? t("platformPage.passengerMpAuditCoverageEligible", { defaultValue: "Elegível" })
      : t("platformPage.passengerMpAuditCoverageNotEligible", { defaultValue: "Não elegível" });
  }
  return "—";
}

function hasChargebackColumnData(rows, columnId, t) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return false;

  switch (columnId) {
    case "chargeback_id":
      return list.some((row) => Boolean(row.chargeback_id));
    case "chargeback_reason":
      return list.some((row) => Boolean(String(row.chargeback_reason || "").trim()));
    case "documentation_status":
      return list.some((row) => {
        const doc = String(row.chargeback_documentation_status || "").trim();
        const reason = String(row.chargeback_reason || "").trim();
        return Boolean(doc) && doc !== reason;
      });
    case "documentation_deadline":
      return list.some((row) => Boolean(row.chargeback_documentation_deadline));
    case "documentation_required":
      return list.some((row) => row.chargeback_documentation_required != null && row.chargeback_documentation_required !== "");
    case "coverage":
      return list.some((row) => fmtCoverage(row, t) !== "—");
    default:
      return true;
  }
}

function buildChargebackOptionalColumns(t) {
  return [
    {
      id: "chargeback_id",
      label: t("platformPage.passengerMpAuditColChargebackId"),
      render: (row) => {
        if (row.chargeback_id) {
          return (
            <Chip size="small" color="error" variant="outlined" label={row.chargeback_id} sx={{ fontWeight: 600 }} />
          );
        }
        return "—";
      },
    },
    {
      id: "chargeback_reason",
      label: t("platformPage.passengerMpAuditColChargebackReason"),
      ...COL.passenger,
      render: (row) => (
        <EllipsisCell maxWidth={160}>{fmtChargebackLabel(row.chargeback_reason, t)}</EllipsisCell>
      ),
    },
    {
      id: "documentation_status",
      label: t("platformPage.passengerMpAuditColDocStatus"),
      render: (row) => fmtChargebackLabel(row.chargeback_documentation_status, t),
    },
    {
      id: "documentation_deadline",
      label: t("platformPage.passengerColChargebackDocDeadline"),
      ...COL.date,
      render: (row) => fmtDateOnly(row.chargeback_documentation_deadline),
    },
    {
      id: "documentation_required",
      label: t("platformPage.passengerColChargebackDocRequired"),
      render: (row) => fmtBool(row.chargeback_documentation_required),
    },
    {
      id: "coverage",
      label: t("platformPage.passengerColChargebackCoverage"),
      ...COL.partner,
      render: (row) => <EllipsisCell maxWidth={110}>{fmtCoverage(row, t)}</EllipsisCell>,
    },
  ];
}

function feeAmountForRow(row, kpiKey) {
  if (kpiKey === "fee_partner") return row.taxa_parceiro;
  if (kpiKey === "fee_mp") return resolveMpFeeAmount(row);
  if (kpiKey === "fee_xvision") return row.taxa_xvision;
  if (kpiKey === "fee_surcharge") return row.sobretaxa;
  if (kpiKey === "fees_total") {
    return (
      Number(row.taxa_parceiro || 0) +
      Number(resolveMpFeeAmount(row) || 0) +
      Number(row.taxa_xvision || 0) +
      Number(row.sobretaxa || 0)
    );
  }
  return null;
}

function EllipsisCell({ children, maxWidth = 180 }) {
  const label = children == null || children === "" ? "—" : String(children);
  return (
    <Typography
      variant="body2"
      noWrap
      title={label === "—" ? undefined : label}
      sx={{ maxWidth, display: "block" }}
    >
      {label}
    </Typography>
  );
}

const COL = {
  workspace: { headSx: { width: 132, minWidth: 132 }, cellSx: { maxWidth: 132 } },
  date: { headSx: { width: 148, minWidth: 148, whiteSpace: "nowrap" }, cellSx: { whiteSpace: "nowrap" } },
  passenger: { headSx: { width: 160, minWidth: 140 }, cellSx: { maxWidth: 180 } },
  route: { headSx: { width: 200, minWidth: 160 }, cellSx: { maxWidth: 220 } },
  value: { headSx: { width: 156, minWidth: 148 }, cellSx: { whiteSpace: "nowrap" } },
  fee: { headSx: { width: 108, minWidth: 108 }, cellSx: { whiteSpace: "nowrap" } },
  status: { headSx: { width: 108, minWidth: 108 } },
  payment: { headSx: { width: 128, minWidth: 120 } },
  partner: { headSx: { width: 104, minWidth: 96 }, cellSx: { maxWidth: 120 } },
};

export default function PassengerKpiDrilldownDrawer({
  open,
  onClose,
  kpiKey,
  title,
  from,
  to,
  dominioId,
  showWorkspaceColumn = true,
  statusLabel,
  mpPaymentType,
  mpInstallments,
  /** Override do fetch (ex.: portal do representante). Default = API admin da plataforma. */
  fetchVendas = getPlatformGlobalPassengerVendas,
  /** Valor no modo workspace / viação (sem breakdown interno Xvision). */
  tenantView = false,
  /** Rótulo da coluna de workspace (ex.: “Agência”). */
  workspaceColumnLabel,
}) {
  const { t } = useTranslation("app");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const rowsPerPage = 25;

  const periodLabel = useMemo(() => formatProductUsagePeriodBR(from, to), [from, to]);
  const fetchKey = useMemo(
    () => [kpiKey, from, to, dominioId || "", mpPaymentType || "", mpInstallments ?? "", page].join("|"),
    [kpiKey, from, to, dominioId, mpPaymentType, mpInstallments, page]
  );

  useEffect(() => {
    if (open) setPage(0);
  }, [open, kpiKey, mpPaymentType, mpInstallments, from, to, dominioId]);

  useEffect(() => {
    if (!open || (!kpiKey && !mpPaymentType && (mpInstallments == null || mpInstallments === ""))) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    setRows([]);
    setTotal(0);

    (async () => {
      try {
        const data = await fetchVendas({
          from,
          to,
          dominioId: dominioId || undefined,
          kpi: kpiKey || undefined,
          mpPaymentType: mpPaymentType || undefined,
          mpInstallments: mpInstallments != null && mpInstallments !== "" ? mpInstallments : undefined,
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          sortBy: "date",
          sortOrder: "desc",
        });
        if (cancelled) return;
        setRows(
          (data?.rows || []).map((row) => ({
            ...row,
            _rowKey: `${row.dominioId || 0}-${row.id}`,
          }))
        );
        setTotal(Number(data?.total || 0));
      } catch (err) {
        if (cancelled) return;
        setRows([]);
        setTotal(0);
        setError(err?.response?.data?.message || err?.message || t("platformPage.passengerKpiDrilldownError"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, kpiKey, from, to, dominioId, mpPaymentType, mpInstallments, page, fetchKey, fetchVendas, t]);

  const isChargebackDrilldown = kpiKey === PASSENGER_KPI_METRICS.chargebacks;

  const columns = useMemo(() => {
    const cols = [];
    if (showWorkspaceColumn) {
      cols.push({
        id: "workspace",
        label: workspaceColumnLabel || t("platformPage.productUsageColWorkspace"),
        ...COL.workspace,
        render: (row) => <EllipsisCell maxWidth={120}>{row.dominio}</EllipsisCell>,
      });
    }

    if (isChargebackDrilldown) {
      cols.push(
        {
          id: "chargeback_date",
          label: t("platformPage.passengerColChargebackDate"),
          ...COL.date,
          render: (row) => fmtDateTime(row.chargeback_em),
        },
        {
          id: "issued_date",
          label: t("platformPage.passengerColIssuedDate"),
          ...COL.date,
          render: (row) => fmtDateTime(row.emitido_em || row.criado_em),
        },
        {
          id: "passenger",
          label: t("passagensPage.colClient"),
          ...COL.passenger,
          render: (row) => (
            <EllipsisCell maxWidth={170}>{row.nome_passageiro || row.client_name}</EllipsisCell>
          ),
        },
        {
          id: "route",
          label: t("passagensPage.colRoute"),
          ...COL.route,
          render: (row) => (
            <EllipsisCell maxWidth={210}>
              {[row.origem, row.destino].filter(Boolean).join(" → ")}
            </EllipsisCell>
          ),
        },
        {
          id: "value",
          label: t("passagensPage.colValue"),
          align: "right",
          ...COL.value,
          render: (row) => <PassengerSaleValueCell row={row} tenantView={tenantView} />,
        },
        {
          id: "chargeback_amount",
          label: t("platformPage.passengerColChargebackAmount"),
          align: "right",
          ...COL.fee,
          render: (row) => (
            <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
              {fmtMoney(row.chargeback_amount ?? row.valor_cobrado)}
            </Typography>
          ),
        },
        ...buildChargebackOptionalColumns(t).filter((col) => hasChargebackColumnData(rows, col.id, t)),
        {
          id: "status",
          label: t("passagensPage.colStatus"),
          ...COL.status,
          render: (row) => (
            <Chip
              size="small"
              variant="outlined"
              color="error"
              label={statusLabel?.(row.status_venda) || row.status_venda || "—"}
              sx={{ maxWidth: "100%" }}
            />
          ),
        },
        {
          id: "payment",
          label: t("passagensPage.colPayment"),
          ...COL.payment,
          render: (row) => <PassengerPaymentCell row={row} t={t} />,
        }
      );
      return cols;
    }

    cols.push(
      {
        id: "date",
        label: t("passagensPage.colDate"),
        ...COL.date,
        render: (row) => fmtDateTime(row.emitido_em || row.criado_em),
      },
      {
        id: "passenger",
        label: t("passagensPage.colClient"),
        ...COL.passenger,
        render: (row) => (
          <EllipsisCell maxWidth={170}>{row.nome_passageiro || row.client_name}</EllipsisCell>
        ),
      },
      {
        id: "route",
        label: t("passagensPage.colRoute"),
        ...COL.route,
        render: (row) => (
          <EllipsisCell maxWidth={210}>
            {[row.origem, row.destino].filter(Boolean).join(" → ")}
          </EllipsisCell>
        ),
      },
      {
        id: "value",
        label: t("passagensPage.colValue"),
        align: "right",
        ...COL.value,
        render: (row) => <PassengerSaleValueCell row={row} tenantView={tenantView} />,
      }
    );
    if (!tenantView && (kpiKey?.startsWith("fee_") || kpiKey === "fees_total")) {
      cols.push({
        id: "fee",
        label: t("platformPage.passengerKpiDrilldownFeeCol"),
        align: "right",
        ...COL.fee,
        render: (row) => (
          <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
            {fmtMoney(feeAmountForRow(row, kpiKey))}
          </Typography>
        ),
      });
    }
    cols.push(
      {
        id: "status",
        label: t("passagensPage.colStatus"),
        ...COL.status,
        render: (row) => (
          <Chip
            size="small"
            variant="outlined"
            label={statusLabel?.(row.status_venda) || row.status_venda || "—"}
            sx={{ maxWidth: "100%" }}
          />
        ),
      },
      {
        id: "payment",
        label: t("passagensPage.colPayment"),
        ...COL.payment,
        render: (row) => <PassengerPaymentCell row={row} t={t} />,
      },
      {
        id: "partner",
        label: t("passagensPage.colPartner"),
        ...COL.partner,
        render: (row) => <EllipsisCell maxWidth={100}>{row.partner_key}</EllipsisCell>,
      }
    );
    return cols;
  }, [isChargebackDrilldown, kpiKey, showWorkspaceColumn, statusLabel, t, rows, tenantView, workspaceColumnLabel]);

  const tableMinWidth = useMemo(() => {
    if (!isChargebackDrilldown) {
      return showWorkspaceColumn ? 1040 : 900;
    }
    const optionalCount = buildChargebackOptionalColumns(t).filter((col) =>
      hasChargebackColumnData(rows, col.id, t)
    ).length;
    const base = showWorkspaceColumn ? 980 : 840;
    return base + optionalCount * 132;
  }, [isChargebackDrilldown, showWorkspaceColumn, rows, t, statusLabel]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      scroll="paper"
      aria-labelledby="passenger-kpi-drilldown-title"
      sx={{ zIndex: (th) => th.zIndex.modal }}
      PaperProps={{
        sx: {
          width: { xs: "calc(100% - 32px)", sm: "min(92vw, 1080px)" },
          maxWidth: "calc(100% - 32px)",
          maxHeight: { xs: "calc(100dvh - 32px)", sm: "calc(100vh - 64px)" },
          m: 2,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      {loading ? <LinearProgress sx={{ flexShrink: 0 }} /> : null}

      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1}
        sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}
      >
        <Box sx={{ minWidth: 0, pr: 1 }}>
          <Typography id="passenger-kpi-drilldown-title" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
            {title}
          </Typography>
          {periodLabel ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {periodLabel}
            </Typography>
          ) : null}
          {isChargebackDrilldown ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
              {t("platformPage.passengerKpiDrilldownChargebackPeriodHint")}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
            {loading
              ? t("common.loading", { defaultValue: "Carregando…" })
              : t("platformPage.passengerKpiDrilldownCount", { count: total.toLocaleString("pt-BR") })}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label={t("common.close", { defaultValue: "Fechar" })} sx={{ flexShrink: 0, mt: -0.5 }}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <Box sx={{ flex: 1, overflow: "auto", px: { xs: 1.5, sm: 2.5 }, py: { xs: 1.5, sm: 2 }, minWidth: 0 }}>
        {loading ? (
          <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        ) : (
          <>
            <AnalyticsDataTable
              rows={rows}
              columns={columns}
              emptyLabel={t("platformPage.passengerKpiDrilldownEmpty")}
              tableMinWidth={tableMinWidth}
            />
            {total > 0 ? (
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
                sx={{ borderTop: 1, borderColor: "divider", mt: 1 }}
                labelDisplayedRows={({ from: f, to: tl, count }) =>
                  t("platformPage.passengerSalesListFooter", { shown: `${f}-${tl}`, total: count })
                }
              />
            ) : null}
          </>
        )}
      </Box>
    </Dialog>
  );
}
